import type { Theme } from './themes';
import { fontFamily, fontSize, radius, spacing } from './tokens';

/**
 * Converts a theme into CSS custom properties for the web client.
 *
 * The web app writes these onto the document root, so switching theme is one attribute change
 * rather than a re-render of every styled component.
 *
 * @param theme - The theme to serialise.
 * @returns A map of custom property names to values.
 */
export function toCssVariables(theme: Theme): Record<string, string> {
  const variables: Record<string, string> = {};

  for (const [role, value] of Object.entries(theme.colors)) {
    variables[`--gm-color-${kebab(role)}`] = value;
  }

  for (const [key, value] of Object.entries(spacing)) {
    variables[`--gm-space-${key}`] = `${value}px`;
  }

  for (const [key, value] of Object.entries(radius)) {
    variables[`--gm-radius-${key}`] = `${value}px`;
  }

  for (const [key, value] of Object.entries(fontSize)) {
    variables[`--gm-font-size-${key}`] = `${value}px`;
  }

  variables['--gm-font-family-system'] = fontFamily.system;
  variables['--gm-font-family-mono'] = fontFamily.mono;

  return variables;
}

/**
 * Serialises CSS custom properties into a declaration block body.
 *
 * @param variables - The properties to serialise.
 * @returns Declarations separated by newlines.
 */
export function cssVariablesToString(variables: Record<string, string>): string {
  return Object.entries(variables)
    .map(([name, value]) => `${name}: ${value};`)
    .join('\n');
}

function kebab(value: string): string {
  return value.replace(/([a-z0-9])([A-Z])/g, '$1-$2').toLowerCase();
}
